
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import React, { useState, useEffect } from 'react';

const LegacyTimeline = () => {

  
  const [activeYear, setActiveYear] = useState(1975);

  const [animatedStats, setAnimatedStats] = useState({
  children: 0,
  staff: 0,
  facilities: 0,
});

  const timelineData = [
    {
      year: 1975,
      title: "Foundation of Hope",
      description: "IGM Children Homes was established with a vision to restore broken lives and provide sanctuary for vulnerable children.",
      image: "/assets/Gallery/20220628_080706.jpg",
      icon: "Home",
      stats: { children: "25", staff: "3", facilities: "1" }
    },
    {
      year: 1985,
      title: "Official Registration",
      description: "Achieved official charitable organization status, establishing legal framework for expanded operations and transparency.",
      image: "/assets/Gallery/IMG_1916.jpg",
      icon: "FileCheck",
      stats: { children: "150", staff: "12", facilities: "2" }
    },
    {
      year: 1995,
      title: "Educational Excellence",
      description: "Launched comprehensive education programs, ensuring every child receives quality schooling and skill development.",
      image: "/assets/Gallery/DSCF9665.jpg",
      icon: "GraduationCap",
      stats: { children: "400", staff: "25", facilities: "4" }
    },
    {
      year: 2005,
      title: "Healthcare Integration",
      description: "Established on-site medical facilities and partnerships with healthcare providers for comprehensive child welfare.",
      image: "/assets/Gallery/Privaram Medical camp.jpg",
      icon: "Heart",
      stats: { children: "800", staff: "45", facilities: "6" }
    },
    {
      year: 2015,
      title: "Digital Transformation",
      description: "Embraced technology for better child tracking, donor engagement, and transparent impact reporting.",
      image: "/assets/Gallery/_DSC4764.jpg",
      icon: "Smartphone",
      stats: { children: "1200", staff: "75", facilities: "8" }
    },
    {
      year: 2025,
      title: "Global Recognition",
      description: "Celebrating 50 years of transformative compassion with half a million lives touched and international partnerships.",
      image: "/assets/Gallery/iGM-3.webp",
      icon: "Award",
      stats: { children: "1500", staff: "120", facilities: "12" }
    }
  ];

  const activeData = timelineData?.find(item => item?.year === activeYear);

  useEffect(() => {
  if (!activeData?.stats) return;

  const finalChildren = parseInt(activeData.stats.children);
  const finalStaff = parseInt(activeData.stats.staff);
  const finalFacilities = parseInt(activeData.stats.facilities);

  const duration = 1200;
  const steps = 40;
  const stepDuration = duration / steps;

  let currentStep = 0;

  const interval = setInterval(() => {
    currentStep++;

    const progress = currentStep / steps;

    setAnimatedStats({
      children: Math.floor(finalChildren * progress),
      staff: Math.floor(finalStaff * progress),
      facilities: Math.floor(finalFacilities * progress),
    });

    if (currentStep >= steps) {
      clearInterval(interval);

      setAnimatedStats({
        children: finalChildren,
        staff: finalStaff,
        facilities: finalFacilities,
      });
    }
  }, stepDuration);

  return () => clearInterval(interval);
}, [activeYear]);

  return (
    <section className="py-16 lg:py-20 bg-muted">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    {/* Header */}
    <div className="text-center mb-12 lg:mb-16">
      <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground mb-4">
        50 Years of Transformative Impact
      </h2>
      <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
        From humble beginnings to global recognition, our journey reflects unwavering commitment to restoring broken lives and creating lasting change.
      </p>
    </div>

    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

      {/* Timeline Navigation */}
      <div className="space-y-5 sm:space-y-6">
        <div className="relative">

          {/* Timeline Line */}
          <div className="absolute left-2 sm:left-6 top-0 bottom-0 w-0.5 bg-border"></div>

          {timelineData?.map((item) => (
            <div
              key={item?.year}
              className={`relative flex items-center space-x-4 p-3 sm:p-4 rounded-xl cursor-pointer transition-warm ${
                activeYear === item?.year
                  ? 'bg-white shadow-warm'
                  : 'hover:bg-white/50'
              }`}
              onClick={() => setActiveYear(item?.year)}
            >
              <div className={`flex items-center justify-center w-3 h-3 rounded-full transition-warm ${
                activeYear === item?.year
                  ? 'bg-primary text-white'
                  : 'bg-primary/80 text-muted-foreground'
              }`}>
               
              </div>

              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-1">
                  <span className={`font-bold text-base sm:text-lg ${
                    activeYear === item?.year ? 'text-primary' : 'text-foreground'
                  }`}>
                    {item?.year}
                  </span>

                  <span className={`font-medium text-base ${
                    activeYear === item?.year ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {item?.title}
                  </span>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {item?.description}
                </p>
              </div>
            </div>
          ))}

        </div>
      </div>

      {/* Active Content */}
      <div className="space-y-5 sm:space-y-6">

        {/* Image */}
        <div className="relative rounded-2xl overflow-hidden shadow-warm-hover">
          <Image
            src={activeData?.image}
            alt={`IGM Children Homes in ${activeData?.year}`}
            className="w-full h-64 sm:h-72 md:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white pr-4">
            <h3 className="font-heading font-bold text-lg sm:text-xl md:text-2xl mb-2">
              {activeData?.title}
            </h3>
            <p className="text-sm sm:text-base text-white/90 leading-relaxed">
              {activeData?.description}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 text-center shadow-warm">
            <div className="text-xl sm:text-2xl font-bold text-primary mb-1">
              {animatedStats.children}
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground">
              Children Served
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 text-center shadow-warm">
            <div className="text-xl sm:text-2xl font-bold text-secondary mb-1">
             {animatedStats.staff}
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground">
              Staff Members
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 text-center shadow-warm">
            <div className="text-xl sm:text-2xl font-bold text-accent mb-1">
             {animatedStats.facilities}
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground">
              Facilities
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</section>

  );
};

export default LegacyTimeline;